import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { dispatch } from '../../utils/event-helpers';
import { MOBILE_BREAKPOINT_PX } from '../../utils/breakpoints';
import type { AscNavbar } from './asc-navbar';

type NavbarHost = AscNavbar & ReactiveControllerHost;

export class NavbarController implements ReactiveController {
  private readonly _host: NavbarHost;
  private _ro: ResizeObserver | undefined;
  private _escListener: ((e: KeyboardEvent) => void) | undefined;

  /** `true` when the host's container width is < 1024 px. */
  isMobile = false;

  constructor(host: NavbarHost) {
    this._host = host;
    host.addController(this);
  }

  hostConnected(): void {
    // Synchronous best-guess before layout is computed.
    this.isMobile = this._host.offsetWidth < MOBILE_BREAKPOINT_PX;
    this._host.toggleAttribute('mobile', this.isMobile);

    this._ro = new ResizeObserver(() => {
      const wasMobile = this.isMobile;
      this.isMobile = this._host.offsetWidth < MOBILE_BREAKPOINT_PX;
      this._host.toggleAttribute('mobile', this.isMobile);
      if (wasMobile !== this.isMobile) {
        if (!this.isMobile && this._host.open) {
          this._host.open = false;
        }
        this._host.requestUpdate();
      }
    });
    this._ro.observe(this._host);

    // Schedule a re-render after the first paint so the ResizeObserver has
    // had a chance to fire with real layout dimensions.
    // This covers the case where offsetWidth was 0 at hostConnected() time
    // (layout not yet computed), ensuring propagation runs with correct values.
    requestAnimationFrame(() => {
      const wasMobile = this.isMobile;
      this.isMobile = this._host.offsetWidth < MOBILE_BREAKPOINT_PX;
      this._host.toggleAttribute('mobile', this.isMobile);
      if (wasMobile !== this.isMobile) {
        this._host.requestUpdate();
      }
    });
    this._host.addEventListener('keydown', this._handleHostKeydown);
  }

  hostDisconnected(): void {
    this._ro?.disconnect();
    this._host.removeEventListener('keydown', this._handleHostKeydown);
    this._removeEscListener();
  }

  hostUpdated(): void {
    if (this._host.open && this.isMobile) {
      this._addEscListener();
    } else {
      this._removeEscListener();
    }
  }

  /** Keydown handler attached to the host element. */
  private _handleHostKeydown = (e: Event): void => {
    const keyEvent = e as KeyboardEvent;
    if (this._host.open && this.isMobile) {
      this._handleFocusTrap(keyEvent);
    } else {
      this._handleArrowNav(keyEvent);
    }
  };

  private _handleArrowNav(e: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const items = Array.from(
      this._host.querySelectorAll<HTMLElement>('asc-nav-item'),
    );
    if (!items.length) return;

    const focused = items.findIndex(
      (el) => el === document.activeElement || el.shadowRoot?.activeElement != null,
    );
    if (focused === -1) return;

    e.preventDefault();
    let next = focused;
    if (e.key === 'ArrowRight') next = (focused + 1) % items.length;
    else if (e.key === 'ArrowLeft') next = (focused - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;

    items[next].focus();
  }

  private _handleFocusTrap(e: KeyboardEvent): void {
    if (e.key !== 'Tab') return;

    const hamburger = this._host.shadowRoot?.querySelector<HTMLElement>('[part="hamburger"]');
    const navSlot = this._host.shadowRoot?.querySelector<HTMLSlotElement>('.nav-items slot:not([name])');
    const actionsSlot = this._host.shadowRoot?.querySelector<HTMLSlotElement>('.actions-area slot[name="actions"]');

    const navItems = (navSlot?.assignedElements({ flatten: true }) ?? []) as HTMLElement[];
    const actionRoots = (actionsSlot?.assignedElements({ flatten: true }) ?? []) as HTMLElement[];

    const slottedItems = [...navItems, ...actionRoots];
    if (slottedItems.length === 0 && !hamburger) return;

    const firstSlotted = slottedItems[0];
    const lastSlotted = slottedItems[slottedItems.length - 1];
    // The hamburger is a shadow-DOM element; when the event crosses the shadow
    // boundary e.target is retargeted to the host, so we detect focus via
    // shadowRoot.activeElement instead.
    const hamburgerFocused = hamburger != null && this._host.shadowRoot?.activeElement === hamburger;
    const active = e.target as Node | null;

    if (e.shiftKey) {
      // Shift+Tab from first slotted item → move focus to hamburger (close button)
      if (firstSlotted != null && active != null && this._isTargetWithinContainer(firstSlotted, active)) {
        e.preventDefault();
        hamburger?.focus();
      } else if (hamburgerFocused) {
        // Shift+Tab from hamburger → wrap to last slotted item
        e.preventDefault();
        lastSlotted?.focus();
      }
    } else {
      // Tab from last slotted item → move focus to hamburger (close button)
      if (lastSlotted != null && active != null && this._isTargetWithinContainer(lastSlotted, active)) {
        e.preventDefault();
        hamburger?.focus();
      } else if (hamburgerFocused) {
        // Tab from hamburger → move to first slotted item
        e.preventDefault();
        firstSlotted?.focus();
      }
    }
  }

  private _addEscListener(): void {
    if (this._escListener) return;
    this._escListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this._host.open) {
        this._host.open = false;
        dispatch(this._host, 'asc-close');
        const hamburger = this._host.shadowRoot?.querySelector<HTMLElement>('[part="hamburger"]');
        hamburger?.focus();
      }
    };
    document.addEventListener('keydown', this._escListener);
  }

  private _removeEscListener(): void {
    if (!this._escListener) return;
    document.removeEventListener('keydown', this._escListener);
    this._escListener = undefined;
  }

  /**
   * Returns whether the keydown target is the same as, inside, or inside the
   * shadow root of the container element. This keeps focus-wrap logic working
   * for custom elements that delegate focus to internal controls.
   */
  private _isTargetWithinContainer(container: HTMLElement, target: Node): boolean {
    if (container === target) return true;
    if (container.contains(target)) return true;
    return container.shadowRoot?.contains(target) ?? false;
  }
}
